/**
 * Demo Data Generators
 *
 * Generates intermediate data states for the animated demo presentation.
 * Each step progressively transforms the data from messy CAD input to clean USD output.
 */

// Exchange rate constant: 1 CAD = 0.74 USD
export const CAD_TO_USD_RATE = 0.74;

// Sample products with messy original descriptions and clean transformed descriptions
export const sampleProducts = [
  {
    code: 'SKU-001',
    messyDesc: 'LED  Panel Light 2x4 -DERA',
    cleanDesc: 'LED Panel Light 2x4',
    qty: 150,
    priceCAD: 45.99,
  },
  {
    code: 'SKU-002',
    messyDesc: 'Elec. Wire  12AWG/500ft  SOUTHWIRE',
    cleanDesc: 'Electrical Wire 12AWG 500ft',
    qty: 75,
    priceCAD: 89.99,
  },
  {
    code: 'SKU-003',
    messyDesc: 'CKT BRKR 20A - Siemens QP',
    cleanDesc: 'Circuit Breaker 20A',
    qty: 200,
    priceCAD: 12.5,
  },
  {
    code: 'SKU-004',
    messyDesc: 'EMT Conduit  1/2in x 10ft',
    cleanDesc: 'Conduit EMT 1/2" 10ft',
    qty: 500,
    priceCAD: 8.75,
  },
  {
    code: 'SKU-005',
    messyDesc: 'JCT BOX 4x4 Steel',
    cleanDesc: 'Junction Box 4x4',
    qty: 300,
    priceCAD: 3.25,
  },
  {
    code: 'SKU-006',
    messyDesc: 'T8 LED Tube  4FT 18W  5000K',
    cleanDesc: 'LED Tube T8 4ft',
    qty: 400,
    priceCAD: 7.99,
  },
  {
    code: 'SKU-007',
    messyDesc: 'Wire Nuts / Connectors  Asst Pack',
    cleanDesc: 'Wire Connector Pack',
    qty: 250,
    priceCAD: 15.99,
  },
  {
    code: 'SKU-008',
    messyDesc: 'Outlet Recept. 15A 125V  WHT',
    cleanDesc: 'Outlet Receptacle 15A',
    qty: 180,
    priceCAD: 2.99,
  },
];

// Messy dates (original) and clean dates (transformed)
export const dateMappings = [
  { messy: 'Jan 15', clean: '2025-01-15' },
  { messy: '22 Jan', clean: '2025-01-22' },
  { messy: '1/29/25', clean: '2025-01-29' },
  { messy: 'Feb 5', clean: '2025-02-05' },
  { messy: '2/12', clean: '2025-02-12' },
];

/**
 * Step 1 Output: Original messy data with CAD prices
 * - Messy dates (various formats)
 * - Messy descriptions (extra spaces, abbreviations)
 * - All prices in CAD
 */
export function generateOriginalData(): string[][] {
  const headers = [
    'Date',
    'Item #',
    'Description',
    'Qty',
    'Currency',
    'Price',
    'Ext. Price',
    'Rebate %',
    'Rebate Amt',
  ];

  const rows: string[][] = [headers];

  for (const dateMap of dateMappings) {
    for (const product of sampleProducts) {
      const total = product.qty * product.priceCAD;
      const rebateRate = 0.03;
      const rebateAmount = total * rebateRate;

      rows.push([
        dateMap.messy,
        product.code,
        product.messyDesc,
        String(product.qty),
        'CAD',
        `$${product.priceCAD.toFixed(2)}`,
        `$${total.toFixed(2)}`,
        '3%',
        `$${rebateAmount.toFixed(2)}`,
      ]);
    }
  }

  return rows;
}

/**
 * Step 2 Output: After parsing dates
 * - Dates normalized to YYYY-MM-DD format
 * - Still has messy descriptions
 * - Still in CAD
 */
export function generateAfterDatesData(): string[][] {
  const headers = [
    'Date',
    'Item #',
    'Description',
    'Qty',
    'Currency',
    'Price',
    'Ext. Price',
    'Rebate %',
    'Rebate Amt',
  ];

  const rows: string[][] = [headers];

  for (const dateMap of dateMappings) {
    for (const product of sampleProducts) {
      const total = product.qty * product.priceCAD;
      const rebateRate = 0.03;
      const rebateAmount = total * rebateRate;

      rows.push([
        dateMap.clean, // Now using clean date format
        product.code,
        product.messyDesc, // Still messy
        String(product.qty),
        'CAD',
        `$${product.priceCAD.toFixed(2)}`,
        `$${total.toFixed(2)}`,
        '3%',
        `$${rebateAmount.toFixed(2)}`,
      ]);
    }
  }

  return rows;
}

/**
 * Step 3 Output: After cleaning text
 * - Dates normalized
 * - Descriptions cleaned (no extra spaces, expanded abbreviations)
 * - Still in CAD
 */
export function generateAfterCleanTextData(): string[][] {
  const headers = [
    'Date',
    'Item #',
    'Description',
    'Qty',
    'Currency',
    'Price',
    'Ext. Price',
    'Rebate %',
    'Rebate Amt',
  ];

  const rows: string[][] = [headers];

  for (const dateMap of dateMappings) {
    for (const product of sampleProducts) {
      const total = product.qty * product.priceCAD;
      const rebateRate = 0.03;
      const rebateAmount = total * rebateRate;

      rows.push([
        dateMap.clean,
        product.code,
        product.cleanDesc, // Now using clean description
        String(product.qty),
        'CAD',
        `$${product.priceCAD.toFixed(2)}`,
        `$${total.toFixed(2)}`,
        '3%',
        `$${rebateAmount.toFixed(2)}`,
      ]);
    }
  }

  return rows;
}

/**
 * Step 4 Output: After currency conversion (CAD to USD)
 * - Dates normalized
 * - Descriptions cleaned
 * - All prices converted from CAD to USD (multiplied by 0.74)
 */
export function generateAfterCurrencyData(): string[][] {
  const headers = [
    'Date',
    'Item #',
    'Description',
    'Qty',
    'Currency',
    'Price',
    'Ext. Price',
    'Rebate %',
    'Rebate Amt',
  ];

  const rows: string[][] = [headers];

  for (const dateMap of dateMappings) {
    for (const product of sampleProducts) {
      const priceUSD = product.priceCAD * CAD_TO_USD_RATE;
      const totalUSD = product.qty * priceUSD;
      const rebateRate = 0.03;
      const rebateAmountUSD = totalUSD * rebateRate;

      rows.push([
        dateMap.clean,
        product.code,
        product.cleanDesc,
        String(product.qty),
        'USD', // Now USD
        `$${priceUSD.toFixed(2)}`,
        `$${totalUSD.toFixed(2)}`,
        '3%',
        `$${rebateAmountUSD.toFixed(2)}`,
      ]);
    }
  }

  return rows;
}

/**
 * Step 5 Output: After AP invoice validation
 * - Same as Step 4 but with validation status
 * - Includes match information
 */
export function generateAfterValidationData(): string[][] {
  const headers = [
    'Date',
    'Item #',
    'Description',
    'Qty',
    'Currency',
    'Price',
    'Ext. Price',
    'Rebate %',
    'Rebate Amt',
    'AP Match',
  ];

  const rows: string[][] = [headers];

  let rowIndex = 0;
  for (const dateMap of dateMappings) {
    for (const product of sampleProducts) {
      const priceUSD = product.priceCAD * CAD_TO_USD_RATE;
      const totalUSD = product.qty * priceUSD;
      const rebateRate = 0.03;
      const rebateAmountUSD = totalUSD * rebateRate;

      // Most rows match, a few are new entries
      const isMatch = rowIndex % 20 !== 3 && rowIndex % 20 !== 17;
      const matchStatus = isMatch ? 'Matched' : 'New Entry';

      rows.push([
        dateMap.clean,
        product.code,
        product.cleanDesc,
        String(product.qty),
        'USD',
        `$${priceUSD.toFixed(2)}`,
        `$${totalUSD.toFixed(2)}`,
        '3%',
        `$${rebateAmountUSD.toFixed(2)}`,
        matchStatus,
      ]);

      rowIndex++;
    }
  }

  return rows;
}

/**
 * Step 6 Output: Final transformed data ready for submission
 * - Normalized headers (snake_case)
 * - Clean numeric values (no currency symbols in numeric columns)
 * - All validations complete
 */
export function generateFinalData(): string[][] {
  const headers = [
    'date',
    'product_code',
    'product_description',
    'quantity',
    'currency',
    'unit_price',
    'total_value',
    'rebate_rate',
    'rebate_amount',
    'validation_status',
  ];

  const rows: string[][] = [headers];

  let rowIndex = 0;
  for (const dateMap of dateMappings) {
    for (const product of sampleProducts) {
      const priceUSD = product.priceCAD * CAD_TO_USD_RATE;
      const totalUSD = product.qty * priceUSD;
      const rebateRate = 0.03;
      const rebateAmountUSD = totalUSD * rebateRate;

      const isMatch = rowIndex % 20 !== 3 && rowIndex % 20 !== 17;
      const matchStatus = isMatch ? 'matched' : 'new';

      rows.push([
        dateMap.clean,
        product.code,
        product.cleanDesc,
        String(product.qty),
        'USD',
        priceUSD.toFixed(2),
        totalUSD.toFixed(2),
        `${(rebateRate * 100).toFixed(1)}%`,
        rebateAmountUSD.toFixed(2),
        matchStatus,
      ]);

      rowIndex++;
    }
  }

  return rows;
}

/**
 * Helper to get the appropriate data state for a given step
 */
export type DataStateKey =
  | 'original'
  | 'afterDates'
  | 'afterCleanText'
  | 'afterCurrency'
  | 'afterValidation'
  | 'final';

export function getDataForStep(step: number): string[][] {
  switch (step) {
    case 1:
      return generateOriginalData();
    case 2:
      return generateAfterDatesData();
    case 3:
      return generateAfterCleanTextData();
    case 4:
      return generateAfterCurrencyData();
    case 5:
      return generateAfterValidationData();
    case 6:
      return generateFinalData();
    default:
      return generateOriginalData();
  }
}

/**
 * Get columns that changed between steps (for highlighting)
 */
export function getChangedColumnsForStep(step: number): number[] {
  switch (step) {
    case 2:
      return [0]; // Date column
    case 3:
      return [2]; // Description column
    case 4:
      return [4, 5, 6, 8]; // Currency, Price, Ext. Price, Rebate Amt
    case 5:
      return [9]; // AP Match column (new)
    case 6:
      return []; // All columns get normalized headers
    default:
      return [];
  }
}
